import { createFileRoute, Outlet, useMatchRoute } from "@tanstack/react-router";
import {
  Stack,
  Tabs,
  PaxTab,
  registerPageTranslations,
  useTranslation,
  useClosableTabs,
  Divider,
  Typography,
} from "@ncino/pax-ui";
import { TagIcon } from "@phosphor-icons/react";
import { Link, useTheme } from "@mui/material";
import { PageHeader } from "@/shared/components/PageHeader";
import { useNavigate } from "@tanstack/react-router";
import {
  GetPoolsForSellPageQuery,
  useGetPoolsForSellPageQuery,
} from "@/services/graphql/reactQuery";
import { SellRouteContext } from "./-internal/SellRouteContext";
import { useCanPublishListingQuery } from "@/services/auth/permissions";
import { useState } from "react";
import { BuyerRequestModal } from "./-internal/BuyerRequestModal";
import { PoolStatus } from "./-internal/sellTableUtil";

type SellTabsProps = {
  openPoolTabs: string[];
  activePoolTab: string | undefined;
  setActivePoolTab: (id: string | undefined) => void;
  closeTab: (id: string) => void;
  currentPoolId: string | null;
  data: NoInfer<GetPoolsForSellPageQuery> | undefined;
};

function SellTabs({
  openPoolTabs,
  activePoolTab,
  setActivePoolTab,
  closeTab,
  currentPoolId,
  data,
}: SellTabsProps) {
  const { t } = useTranslation("sell");
  const navigate = useNavigate();

  const currentPoolStatus = data?.pools.find((p) => p.id === currentPoolId)?.listing?.listingStage;
  const navigateToTearSheet =
    currentPoolStatus !== PoolStatus.draft && currentPoolStatus !== PoolStatus.activeListing;

  const handleTabChange = (_: any, value: string) => {
    setActivePoolTab(value);
    navigate({
      to: "/sell/$poolId",
      params: { poolId: value },
      search: { tab: navigateToTearSheet ? "tearsheet" : "analytics" },
    });
  };

  const getPoolLabel = (poolId: string) =>
    data?.pools.find((p) => p.id === poolId)?.poolName ?? poolId;

  const goToHomeTab = () => {
    setActivePoolTab(undefined);
    navigate({ to: "/sell" });
  };

  return (
    <Stack direction="row" alignItems="center">
      <Tabs
        value={currentPoolId ? false : "view"}
        aria-label={t("sellPage.viewPoolsTab")}
        onChange={goToHomeTab}
        sx={{ flexShrink: 0 }}
      >
        <PaxTab fillOnSelect label={t("sellPage.viewPoolsTab")} value="view" />
      </Tabs>
      {openPoolTabs.length > 0 && (
        <>
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{ mx: 1, alignSelf: "center", whiteSpace: "nowrap" }}
          >
            {t("sellPage.selectedTabs")}
          </Typography>
          <Tabs
            value={activePoolTab && openPoolTabs.includes(activePoolTab) ? activePoolTab : false}
            variant="scrollable"
            onChange={handleTabChange}
          >
            {openPoolTabs.map((poolId) => (
              <PaxTab
                key={poolId}
                value={poolId}
                label={getPoolLabel(poolId)}
                closeIcon
                fillOnSelect
                onClose={() => closeTab(poolId)}
                onMiddleClick={() => closeTab(poolId)}
              />
            ))}
          </Tabs>
        </>
      )}
    </Stack>
  );
}

function BuyerRequestsMessage({ setModalOpen }: { setModalOpen: (b: boolean) => void }) {
  const { t } = useTranslation("sell");
  const theme = useTheme();
  return (
    <Typography variant="body1">
      {t("sellPage.viewBuyerRequestTitle") + " "}
      <Link
        variant="body2"
        component="button"
        type="button"
        onClick={() => setModalOpen(true)}
        underline="none"
        sx={{
          color: theme.palette.primary.main,
          fontWeight: theme.typography.fontWeightMedium,
        }}
      >
        {t("sellPage.viewBuyerRequestLink") + " ->"}
      </Link>
    </Typography>
  );
}

const RouteComponent = () => {
  const { t } = useTranslation("sell");
  const matchRoute = useMatchRoute();
  const navigate = useNavigate();

  const poolMatch = matchRoute({ to: "/sell/$poolId" }) as { poolId: string } | false;
  const currentPoolId = poolMatch ? poolMatch.poolId : null;
  const { data: canPublishListing = false } = useCanPublishListingQuery();
  const [buyerInterestModalOpen, setbuyerInterestModalOpen] = useState(false);

  const { data, isLoading } = useGetPoolsForSellPageQuery({
    isActiveOnly: true,
  });

  const currentPoolStatus = data?.pools.find((p) => p.id === currentPoolId)?.listing?.listingStage;
  const navigateToTearSheet =
    currentPoolStatus !== PoolStatus.draft && currentPoolStatus !== PoolStatus.activeListing;

  const {
    tabs: openPoolTabs,
    active: activePoolTab,
    setActive: setActivePoolTab,
    closeTab,
    addTab: addPoolTab,
  } = useClosableTabs<string>({
    initialTabs: currentPoolId ? [currentPoolId] : undefined,
    onTabClose: ({ wasActive, wasFinalTab, newActiveTabId }) => {
      if (!wasActive) {
        return;
      }
      if (wasFinalTab || !newActiveTabId) {
        navigate({ to: "/sell" });
      } else {
        navigate({
          to: "/sell/$poolId",
          params: { poolId: newActiveTabId },
          search: { tab: navigateToTearSheet ? "tearsheet" : "analytics" },
        });
      }
    },
  });

  return (
    <>
      <PageHeader
        title={t("sellPage.title")}
        subheader={t("sellPage.subtitle")}
        removeBottomPadding
        actionsRight={
          canPublishListing ? (
            <BuyerRequestsMessage setModalOpen={setbuyerInterestModalOpen} />
          ) : undefined
        }
        centerActionsRight
        actionsBottom={
          <SellTabs
            openPoolTabs={openPoolTabs}
            activePoolTab={activePoolTab}
            setActivePoolTab={setActivePoolTab}
            closeTab={closeTab}
            currentPoolId={currentPoolId}
            data={data}
          />
        }
      />
      <SellRouteContext.Provider
        value={{
          addTab: addPoolTab,
          setActive: setActivePoolTab,
          openTabs: openPoolTabs,
          poolQuery: data,
          queryLoading: isLoading,
          page: 0,
          setPage: () => null,
        }}
      >
        <Outlet />
      </SellRouteContext.Provider>
      <BuyerRequestModal
        open={buyerInterestModalOpen}
        onClose={() => setbuyerInterestModalOpen(false)}
      />
    </>
  );
};

export const Route = createFileRoute("/sell")({
  loader: async () => {
    const { default: en_US } = await import("./locales/en_US.json");
    registerPageTranslations("sell", { en_US });
  },
  component: RouteComponent,
  staticData: {
    breadcrumb: { title: ({ t }) => t("breadcrumbs.sell"), icon: TagIcon },
  },
});
